import Styles from "../styles/Main.module.css";
import Beans from "../images/coffee_beans.jpg";
import Coffee from "../images/cut_coffee.jpg";
import Cookie from "../images/nadali_cookie.jpg";
import Button from "./Button";
import { useNavigate } from 'react-router-dom';
import FloatingBeans from "../images/Beans.png";

import { useState, useRef, useEffect } from 'react';

function MapSection() {
  const [loadMap, setLoadMap] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoadMap(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }   // start loading a bit before it's visible
    );

    if (mapRef.current) observer.observe(mapRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={mapRef} style={{ height: '100%', width: '100%' }}>
      {loadMap ? (
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3072.7392692983776!2d19.877729176743536!3d39.63307410296117!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135b5d49106383a7%3A0x626d551ba8813eb!2sNADALI%20COFFEE%20%26%20BAKERY!5e0!3m2!1sen!2sgr!4v1770757914054!5m2!1sen!2sgr"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Nadali Coffee & Bakery Location"
        ></iframe>
      ) : (
        <div style={{ 
          height: '100%', 
          background: '#f5f5f5', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          Loading map...
        </div>
      )}
    </div>
  );
}

export default function Main() {
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const updateFallDistance = () => {
      if (!section) return;
      const height = section.getBoundingClientRect().height;
      section.style.setProperty('--fall-distance', `${height * 1.4}px`);
    };

    updateFallDistance();

    const resizeObserver = new ResizeObserver(updateFallDistance);
    resizeObserver.observe(section);

    window.addEventListener('resize', updateFallDistance);
    window.addEventListener('orientationchange', updateFallDistance);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateFallDistance);
      window.removeEventListener('orientationchange', updateFallDistance);
    };
  }, []);

  return (
    <section
      className={Styles.MainSection}
      ref={sectionRef}
      style={{ '--fall-distance': '160dvh' }}
    >
      {/* Coffee Beans Rain */}
      <div className={Styles.BeanRain}>
        {Array.from({ length: 18 }).map((_, i) => (
          <img
            key={i}
            src={FloatingBeans}
            alt="floating bean"
            className={Styles.Bean}
            style={{
              left: `${Math.random() * 100}%`,
              animationDuration: `${8 + Math.random() * 8}s, ${4 + Math.random() * 6}s`,
              animationDelay: `-${Math.random() * 12}s`,
              width: `${24 + Math.random() * 30}px`,
              opacity: 0.7 + Math.random() * 0.2,
            }}
          />
        ))}
      </div>

      {/* First block: Coffee Beans */}
      <div className={`${Styles.Container} ${Styles.Reverse}`}>
        <div className={Styles.Image}>
          <img src={Beans} alt="Freshly roasted coffee beans" />
        </div>
        <div className={`${Styles.TextDiv} ${Styles.Light}`}>
          <h3>Hand-Selected & Expertly Roasted</h3>
          <p>
            Every bean we use is carefully chosen from the world’s finest estates and roasted in small batches right here on Corfu. 
            We roast to perfection to bring out rich, balanced flavors — never burnt, never flat. 
            Taste the difference in every cup.
          </p>
          <p>
            <strong>Ready to experience it?</strong> Come visit us and explore our selection of freshly roasted beans and specialty drinks.
          </p>
          <Button 
            variant="primary" 
            onClick={() => navigate('/menu')}
          >
            View Our Menu
          </Button>
        </div>
      </div>

      {/* Second block: Espresso Machine / Pour */}
      <div className={`${Styles.Container} ${Styles.LightSection}`}>
        <div className={Styles.TextDiv}>
          <h3>Crafted with Precision, Served with Passion</h3>
          <p>
            Our baristas treat every shot like a small ritual. Freshly ground beans, exact 9-bar pressure, and a steady hand create that signature thick crema and layered flavor you deserve.
          </p>
          <p>
            Whether it's a classic espresso, velvety cappuccino, or your personal favorite — we prepare it with care every single time.
          </p>
          <p>
            <strong>Come experience the difference yourself.</strong> We're waiting for you with a warm welcome and a perfect cup.
          </p>
          <Button 
            variant="primary" 
            onClick={() => navigate('/menu')}
          >
            See Full Menu
          </Button>
        </div>
        <div className={Styles.Image}>
          <img src={Coffee} alt="Barista preparing perfect espresso" />
        </div>
      </div>

      {/* Third block: Cookie */}
      <div className={`${Styles.Container} ${Styles.Reverse}`}>
        <div className={Styles.Image}>
          <img src={Cookie} alt="Freshly baked Nadali cookie" />
        </div>
        <div className={`${Styles.TextDiv} ${Styles.Light}`}>
          <h3>Homemade Goodness You Can Taste</h3>
          <p>
            Our cookies are baked fresh daily using real butter, premium chocolate, and time-tested recipes. 
            Crispy edges, soft centers, and just the right amount of sweetness — made to pair perfectly with our coffee.
          </p>
          <p>
            Take a box home, enjoy one warm from the oven, or share with friends. 
            Simple ingredients, serious care — that’s the Nadali way.
          </p>
          <p>
            <strong>Craving something sweet?</strong> Come by and treat yourself — our pastries and cookies disappear fast!
          </p>
          <Button 
            variant="primary" 
            onClick={() => navigate('/menu')}
          >
            Check Our Menu
          </Button>
        </div>
      </div>
      
      {/* Location / Visit Us Section */}
      <div className={`${Styles.Container} ${Styles.LightSection}`}>
        <div className={`${Styles.TextDiv}`}>
          <h3>Come Find Us</h3>
          <p>
            We're right here on Corfu waiting with fresh coffee, warm cookies, and that cozy corner you've been craving.
          </p>
          <p>
            Drop by for your morning espresso, an afternoon treat, or just to say hi. 
            Our doors (and oven) are always open.
          </p>
          <p>
            <strong>Pro tip:</strong> The cookies are best when they're still warm. Come claim yours before they're gone.
          </p>
          <Button 
            variant="primary" 
            onClick={() => navigate('/menu')}
          >
            Explore Our Menu
          </Button>
        </div>

        <div className={Styles.Maps}>
          <MapSection />
        </div>
      </div>
    </section>
  );
}